import {
  useMutation,
  useQueryClient,
  useIsMutating,
} from "@tanstack/react-query";

import type { CreateConversationPayload } from "~/conversations/api/create-conversation";
import { createConversation } from "~/conversations/api/create-conversation";
import { getConversationQueryOptions } from "~/conversations/queries/get-conversation-query";
import { getConversationsQueryOptions } from "~/conversations/queries/get-conversations-query";

type CreateConversationOptions = {
  signal: AbortSignal;
};

function useCreateConversationMutation({ signal }: CreateConversationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    retry: false,
    throwOnError: false,
    mutationKey: ["workspace", "create", "conversation"],
    async mutationFn(payload: CreateConversationPayload) {
      const newConversation = await createConversation(payload, { signal });

      await queryClient.ensureQueryData(
        getConversationQueryOptions({
          conversationId: newConversation.id,
        }),
      );

      return newConversation;
    },
    onSuccess(conversation, { workspaceId }) {
      queryClient.invalidateQueries({
        queryKey: getConversationsQueryOptions({ workspaceId }).queryKey,
        exact: true,
      });
    },
  });
}

function useIsConversationMutating({
  workspaceId,
}: Partial<Pick<CreateConversationPayload, "workspaceId">>) {
  const mutationCount = useIsMutating({
    status: "pending",
    mutationKey: ["workspace", "create", "conversation"],
    predicate: (mutation) => {
      if (
        !mutation.state.variables ||
        typeof mutation.state.variables !== "object"
      )
        return false;

      if ("workspaceId" in mutation.state.variables) {
        return mutation.state.variables.workspaceId === workspaceId;
      }
      return false;
    },
  });
  return mutationCount > 0;
}

export { useCreateConversationMutation, useIsConversationMutating };
